/**
 * Data layer for `igaming-responsible-gambling-filters-self-excluded`. Thin typed fetch wrappers over /api/v1/igaming-responsible-gambling-filters-self-excluded.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingResponsibleGamblingFiltersSelfExcludedRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingResponsibleGamblingFiltersSelfExcludedResponse {
	count: number;
	items: IgamingResponsibleGamblingFiltersSelfExcludedRow[];
}

export interface CreateIgamingResponsibleGamblingFiltersSelfExcludedBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-responsible-gambling-filters-self-excluded";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingResponsibleGamblingFiltersSelfExcludedResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingResponsibleGamblingFiltersSelfExcludedResponse>(res);
}

export async function create(body: CreateIgamingResponsibleGamblingFiltersSelfExcludedBody): Promise<IgamingResponsibleGamblingFiltersSelfExcludedRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingResponsibleGamblingFiltersSelfExcludedRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingResponsibleGamblingFiltersSelfExcludedQueryKeys = {
	all: ["igaming-responsible-gambling-filters-self-excluded"] as const,
	list: () => [...igamingResponsibleGamblingFiltersSelfExcludedQueryKeys.all] as const,
	detail: (id: string) => [...igamingResponsibleGamblingFiltersSelfExcludedQueryKeys.all, id] as const,
};
